import { isEmpty } from "lodash";
import type { useInvestorInfo } from "../hooks";
import { ClientHoldingPattern, ClientStatus } from "../types";

export const TAX_STATUS_FILTER_LABEL_MAP: Record<string, string> = {
  resident_individual: "Individual",
  nri: "NRI",
  on_behalf_of_minor: "Minor",
  huf: "HUF",
  company: "Company",
  aop: "Company",
  partnership_firm: "Company",
  body_corporate: "Company",
  trust: "Company",
  society: "Company",
  others: "Others",
  dfi: "Company",
  sole_proprietorship: "Company",
  nri_repatriable_nre: "NRI",
  ocb: "Company",
  fii: "Company",
  nri_repatriable_nro: "NRI",
  overseas_corp_body_others: "Company",
  nri_child: "NRI",
  nri_huf_nro: "NRI",
  nri_minor_nro: "NRI",
  nri_huf_nre: "NRI",
  providend_fund: "Others",
  super_annuation_fund: "Others",
  gratuity_fund: "Others",
  pension_fund: "Others",
  mutual_funds_fof_schemes: "Others",
  nps_trust: "Others",
  global_development_network: "Others",
  fcra: "Others",
  qfi_individual: "Others",
  qfi_minors: "Others",
  qfi_corporate: "Others",
  qfi_pension_funds: "Others",
  qfi_hedge_funds: "Others",
  qfi_mutual_funds: "Others",
  llp: "Company",
  non_profit_organization_npo: "Others",
  public_limited_company: "Company",
  private_limited_company: "Company",
  unlisted_company: "Company",
  mutual_funds: "Others",
  fpi_category_i: "Others",
  fpi_category_ii: "Others",
  fpi_category_iii: "Others",
  financial_institutions: "Others",
  body_of_individuals: "Others",
  insurance_company: "Others",
  oci_repatriation: "NRI",
  oci_non_repatriation: "NRI",
  person_of_indian_origin: "NRI",
  government_body: "Others",
  defence_establishment: "Others",
  non_government_organisation: "Others",
  bank_cooperative_bank: "Others",
  artificial_juridical_person: "Others",
  seafarer_nre: "NRI",
  seafarer_nro: "NRI",
  local_authority: "Others",
};

export const TAX_STATUSES = Object.keys(TAX_STATUS_FILTER_LABEL_MAP);

export const NRI_TAX_STATUSES = new Set(
  Object.entries(TAX_STATUS_FILTER_LABEL_MAP)
    .filter(([, value]) => value.toLowerCase().includes("nri"))
    .map(([key]) => key)
);

export const ACCOUNT_TYPE_OPTIONS = [
  {
    label: "Resident individual",
    value: "resident_individual",
    description: "Lives in India",
  },
  { label: "NRI", value: "nri", description: "Lives abroad" },
  {
    label: "Minor",
    value: "on_behalf_of_minor",
    description: "Under 18, with a guardian",
  },
];

export const TRANSACTION_CLIENT_STATES: ClientStatus[] = [
  "ACTIVE",
  "UPDATE_PENDING",
];

export const INACTIVE_CLIENT_STATUSES: ClientStatus[] = [
  "LEAD",
  "VERIFICATION_PENDING",
  "SETUP_PENDING",
];

export const ACTIVE_STATUSES: ClientStatus[] = [
  "ACTIVE",
  "UPDATE_PENDING",
  "UPDATE_UNDER_REVIEW",
  "AUTHENTICATION_PENDING",
];

export const FAMILY_CLIENT_STATES: ClientStatus[] = [
  ...ACTIVE_STATUSES,
  "SETUP_PENDING",
];

export const JOINT_CLIENT_HOLDING_PATTERNS: ClientHoldingPattern[] = [
  "anyone_or_survivor",
  "joint",
];

type OnboardingData = ReturnType<typeof useInvestorInfo>["data"];

export const ONBOARDING_STEPS: {
  label: string;
  id: string;
  completed: (data: OnboardingData) => boolean;
}[] = [
  {
    id: "personal-details",
    label: "Personal details",
    completed: (data) => !!data.profile?.gender,
  },
  {
    id: "address",
    label: "Address",
    completed: (data) => !!data.address,
  },
  {
    id: "bank-details",
    label: "Bank details",
    completed: (data) => !isEmpty(data.bank),
  },
  {
    id: "financial-profile",
    label: "Financial profile",
    completed: (data) => !!data.profile?.pep_details,
  },
  {
    id: "nominee",
    label: "Nominee",
    completed: (data) =>
      !!data.nominee?.[0] || data.account?.nomination_opt === "N",
  },
  {
    id: "declaration",
    label: "Declaration",
    completed: (data) =>
      !!data.contact &&
      (!!data.contact.autofilled ||
        (!!data.contact.number &&
          !!data.contact.phone_belongs_to &&
          !!data.contact.email &&
          !!data.contact.email_belongs_to)),
  },
  {
    id: "notify-client",
    label: "Notify client",
    completed: () => false,
  },
];

// Done once the onboarding checklist has none of these codes left
const clearsChecklist = (codes: string[]) => (data: OnboardingData) =>
  !!data.checklist &&
  !data.checklist.missing.some((item) => codes.includes(item.code));

export const CHECKLIST_STEP_CODES: Record<string, string[]> = {
  "personal-details": ["DOB_MISSING", "MINOR_NOT_UNDER_18"],
  guardian: [
    "GUARDIAN_MISSING",
    "GUARDIAN_RELATIONSHIP_MISSING",
    "GUARDIAN_PAN_MISSING",
    "GUARDIAN_DOB_MISSING",
    "GUARDIAN_NOT_ADULT",
  ],
  "guardian-profile": ["GUARDIAN_FINANCIAL_PROFILE_MISSING"],
  address: ["ADDRESS_MISSING", "FOREIGN_ADDRESS_MISSING"],
  "tax-residency": ["TAX_RESIDENCY_MISSING", "BIRTH_DETAILS_MISSING"],
  "bank-details": [
    "BANK_ACCOUNT_MISSING",
    "BANK_ACCOUNT_NOT_VERIFIED",
    "NRI_ACCOUNT_TYPE_MISSING",
    "NRI_BANK_TYPE_MISMATCH",
  ],
  nominee: ["NOMINATION_NOT_ALLOWED"],
  declaration: ["CONTACT_MISSING", "CONTACT_DECLARATION_MISSING"],
  documents: [
    "DOCUMENT_MISSING_MINOR_ID_PROOF",
    "DOCUMENT_MISSING_CANCELLED_CHEQUE",
    "DOCUMENT_MISSING_COURT_ORDER",
  ],
};

/** The step a checklist code sends the user to, if it has one */
export const getChecklistStepId = (code: string) =>
  Object.entries(CHECKLIST_STEP_CODES).find(([, codes]) =>
    codes.includes(code)
  )?.[0];

// NRI clients get an overseas address and an extra Tax residency step
export const NRI_ONBOARDING_STEPS: typeof ONBOARDING_STEPS = [
  ONBOARDING_STEPS[0],
  {
    ...ONBOARDING_STEPS[1],
    label: "Overseas address",
    completed: clearsChecklist(["FOREIGN_ADDRESS_MISSING"]),
  },
  {
    id: "tax-residency",
    label: "Tax residency",
    // The checklist can leave these codes out before the profile exists
    completed: (data) =>
      !!data.profile?.first_tax_residency?.country &&
      clearsChecklist(CHECKLIST_STEP_CODES["tax-residency"])(data),
  },
  ...ONBOARDING_STEPS.slice(2),
];

const stepById = (id: string) => ONBOARDING_STEPS.find((s) => s.id === id)!;

// Minor accounts are run by a guardian: no Nominee step, the financial profile
// sits inside Minor details, and documents are collected at the end
export const MINOR_ONBOARDING_STEPS: typeof ONBOARDING_STEPS = [
  { ...stepById("personal-details"), label: "Minor details" },
  {
    id: "guardian",
    label: "Guardian",
    completed: clearsChecklist(CHECKLIST_STEP_CODES.guardian),
  },
  stepById("address"),
  stepById("bank-details"),
  stepById("declaration"),
  {
    id: "documents",
    label: "Documents",
    completed: clearsChecklist(CHECKLIST_STEP_CODES.documents),
  },
  { ...stepById("notify-client"), label: "Notify guardian" },
];

// When the guardian isn't your client yet, their profile is collected too
export const MINOR_NEW_GUARDIAN_ONBOARDING_STEPS: typeof ONBOARDING_STEPS = [
  ...MINOR_ONBOARDING_STEPS.slice(0, 2),
  {
    id: "guardian-profile",
    label: "Guardian profile",
    completed: clearsChecklist(CHECKLIST_STEP_CODES["guardian-profile"]),
  },
  ...MINOR_ONBOARDING_STEPS.slice(2),
];

// Defaults saved for a minor's financial profile
export const MINOR_FINANCIAL_PROFILE_DEFAULTS = {
  occupation: "student",
  income_slab: "upto_1lakh",
  source_of_wealth: "others",
  pep_details: "not_applicable",
} as const;

// Countries that don't issue a Tax Identification Number, per
// TIN_Format_Foreign_Countries.xlsx
export const NO_TIN_COUNTRIES = [
  "AE", // United Arab Emirates
  "BH", // Bahrain
  "BM", // Bermuda
  "BS", // Bahamas
  "KY", // Cayman Islands
  "LB", // Lebanon
  "MC", // Monaco
  "MO", // Macao
  "MS", // Montserrat
  "SX", // Sint Maarten
  "TC", // Turks and Caicos Islands
  "VG", // Virgin Islands (British)
];

export const isTinNotApplicable = (country?: string | null) =>
  !!country && NO_TIN_COUNTRIES.includes(country);

/**
 * Individual TIN structure per country, from the "TIN structure Individuals"
 * column of TIN_Format_Foreign_Countries.xlsx. Matched after dropping spaces,
 * hyphens, dots, slashes and brackets; other countries only get the generic
 * check.
 */
export const TIN_FORMATS: Record<string, { pattern: RegExp; example: string }> =
  {
    AD: { pattern: /^[A-Z]\d{6}[A-Z]$/, example: "F-123456-Z" },
    AG: { pattern: /^\d{8}$/, example: "12345678" },
    AI: { pattern: /^\d{10}$/, example: "1234567890" },
    AL: { pattern: /^[A-Z]\d{8}[A-Z]$/, example: "J12345678N" },
    AM: { pattern: /^\d{8}$/, example: "12345678" },
    AR: { pattern: /^\d{11}$/, example: "20123456789" },
    AT: { pattern: /^\d{9}$/, example: "12-345/6789" },
    AU: { pattern: /^\d{8,9}$/, example: "123 456 789" },
    AW: { pattern: /^\d{8}$/, example: "12345678" },
    AZ: { pattern: /^\d{10}$/, example: "1525855852" },
    BB: { pattern: /^\d{13}$/, example: "1234567890123" },
    BE: { pattern: /^\d{11}$/, example: "12345678901" },
    BG: { pattern: /^\d{9,10}$/, example: "1234567890" },
    BN: { pattern: /^\d{8}$/, example: "12345678" },
    BR: { pattern: /^\d{11}$/, example: "123.456.789-01" },
    BZ: { pattern: /^\d{6}$/, example: "123456" },
    CA: { pattern: /^\d{9}$/, example: "123 456 789" },
    CH: { pattern: /^\d{13}$/, example: "756.1234.5678.97" },
    CK: { pattern: /^\d{5}$/, example: "12345" },
    CL: { pattern: /^\d{8}[0-9K]$/, example: "12345678K" },
    CN: { pattern: /^[A-Z0-9]{18}$/, example: "110101199001011234" },
    CO: { pattern: /^\d{13}$/, example: "1234567890123" },
    CR: { pattern: /^\d{9}$/, example: "1-2345-6789" },
    CW: { pattern: /^\d{9}$/, example: "123456789" },
    CY: { pattern: /^\d{8}[A-Z]$/, example: "12345678L" },
    CZ: { pattern: /^\d{9}$/, example: "123456/789" },
    DE: { pattern: /^\d{11}$/, example: "12345678901" },
    DK: { pattern: /^\d{10}$/, example: "123456-7890" },
    DM: { pattern: /^\d{6}$/, example: "007593" },
    EC: { pattern: /^\d{13}$/, example: "1719304188001" },
    EE: { pattern: /^\d{11}$/, example: "12345678901" },
    // NIE too, which foreign residents get in place of a DNI
    ES: { pattern: /^(\d{8}|[XYZ]\d{7})[A-Z]$/, example: "12345678L" },
    // 6 digits, then +, - or A, 3 digits and a letter or digit
    FI: { pattern: /^\d{6}[+A]?\d{3}[A-Z0-9]$/, example: "131052-308T" },
    FO: { pattern: /^\d{9}$/, example: "123456789" },
    FR: { pattern: /^\d{13}$/, example: "12 34 567 890 123" },
    GB: {
      // UTR, or National Insurance number
      pattern: /^(\d{10}|[A-Z]{2}\d{6}[A-D])$/,
      example: "1234567890",
    },
    GD: { pattern: /^\d{6}$/, example: "123456" },
    GE: { pattern: /^\d{9}$/, example: "123456789" },
    GG: { pattern: /^GY\d{6}$/, example: "GY123456" },
    GH: { pattern: /^\d{11}$/, example: "12345678901" },
    GI: { pattern: /^\d{1,6}$/, example: "123456" },
    GL: { pattern: /^\d{10}$/, example: "1234567890" },
    GR: { pattern: /^\d{9}$/, example: "123456789" },
    HK: { pattern: /^[A-Z]{1,2}\d{6}[0-9A]$/, example: "A123456(7)" },
    HR: { pattern: /^\d{11}$/, example: "12345678901" },
    HU: { pattern: /^\d{10}$/, example: "1234567890" },
    ID: { pattern: /^\d{15}$/, example: "12.345.678.9-012.345" },
    IE: { pattern: /^\d{7}[A-Z]{1,2}$/, example: "1234567T" },
    IL: { pattern: /^\d{9}$/, example: "123456789" },
    IM: {
      pattern: /^([A-Z]\d{8}|[A-Z]{2}\d{6}[A-Z])$/,
      example: "H12345678",
    },
    IS: { pattern: /^\d{10}$/, example: "123456-7890" },
    IT: {
      pattern: /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/,
      example: "RSSMRA85T10A562S",
    },
    JE: {
      pattern: /^([A-Z]{2}\d{6}[A-Z]|\d{10})$/,
      example: "JY123456A",
    },
    JM: { pattern: /^\d{9}$/, example: "123456789" },
    JP: { pattern: /^\d{12}$/, example: "123456789012" },
    KE: { pattern: /^[A-Z]\d{9}[A-Z]$/, example: "A123456789X" },
    KN: { pattern: /^\d+$/, example: "123456" },
    KR: { pattern: /^\d{13}$/, example: "123456-1234567" },
    KW: { pattern: /^\d{12}$/, example: "123456789012" },
    KZ: { pattern: /^\d{12}$/, example: "123456789012" },
    LC: { pattern: /^\d{6}$/, example: "123456" },
    // Leading zeros may be left out
    LI: { pattern: /^\d{1,12}$/, example: "123456789012" },
    LT: { pattern: /^\d{11}$/, example: "12345678901" },
    LU: { pattern: /^\d{13}$/, example: "1234567890123" },
    LV: { pattern: /^\d{11}$/, example: "381234-56789" },
    MD: { pattern: /^\d{13}$/, example: "2001234567890" },
    MH: { pattern: /^\d{7}$/, example: "08612-04" },
    MT: { pattern: /^(\d{7}[A-Z]|\d{8})$/, example: "1234567M" },
    MU: { pattern: /^\d{8}$/, example: "12345678" },
    MV: { pattern: /^\d{7}$/, example: "1000001" },
    MX: { pattern: /^[A-Z]{4}\d{6}[A-Z0-9]{3}$/, example: "ABCD123456EF1" },
    MY: { pattern: /^(IG\d{9,11}|\d{11,13})$/, example: "IG115002000" },
    NG: { pattern: /^\d{12}$/, example: "12345678-0001" },
    NL: { pattern: /^\d{9}$/, example: "123456789" },
    NO: { pattern: /^\d{11}$/, example: "01129955131" },
    NR: { pattern: /^\d{9}$/, example: "123-456-789" },
    NZ: { pattern: /^\d{8,9}$/, example: "123-456-789" },
    OM: { pattern: /^\d{7}$/, example: "1234567" },
    PA: { pattern: /^\d{1,13}$/, example: "8-100-678" },
    PE: { pattern: /^\d{11}$/, example: "10123456789" },
    PK: { pattern: /^\d{13}$/, example: "12345-1234567-1" },
    PL: { pattern: /^\d{11}$/, example: "02070803628" },
    PT: { pattern: /^\d{9}$/, example: "123456789" },
    QA: { pattern: /^\d{10}$/, example: "1234567890" },
    RO: { pattern: /^\d{13}$/, example: "1234567890123" },
    RU: { pattern: /^\d{12}$/, example: "123456789012" },
    SA: { pattern: /^\d{10}$/, example: "1234567890" },
    SC: { pattern: /^\d{9}$/, example: "123456789" },
    SE: { pattern: /^\d{10}$/, example: "123456-7890" },
    SG: { pattern: /^[A-Z]\d{7}[A-Z]$/, example: "S1234567D" },
    SI: { pattern: /^\d{8}$/, example: "12345678" },
    SK: { pattern: /^\d{10}$/, example: "1234567890" },
    SM: { pattern: /^\d{2,}$/, example: "123456789" },
    TR: { pattern: /^\d{10}$/, example: "1230005284" },
    TT: { pattern: /^\d{10}$/, example: "1234567890" },
    UA: { pattern: /^\d{10}$/, example: "1234567890" },
    // Not in the sheet: SSN / ITIN
    US: { pattern: /^\d{9}$/, example: "123-45-6789" },
    UY: { pattern: /^\d{9}[A-Z]$/, example: "123456789D" },
    VC: { pattern: /^\d+$/, example: "123456" },
    VU: { pattern: /^\d{6}$/, example: "200006" },
    ZA: { pattern: /^\d{10}$/, example: "0123456789" },
  };

export const NRI_BANK_ACCOUNT_TYPE = [
  { label: "NRE", value: "NE" },
  { label: "NRO", value: "NO" },
];
